import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useCart } from '../contexts/CartContext';
import Footer from '../components/Footer';
import { imgSrc } from '../utils/img';
import './Cart.css';

function toPriceCents(item) {
  if (typeof item.priceCents === 'number') return item.priceCents;
  const n = parseFloat(String(item.price || '0').replace('$', '').replace('+', ''));
  return Math.round(n * 100);
}

function isValidEmail(email) {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email || '');
}

function absoluteForStripe(u) {
  if (!u) return new URL('/nails-preview-placeholder.jpg', window.location.origin).href;
  if (/^https?:\/\//i.test(u)) return u;
  const resolved = imgSrc(u);
  try {
    return new URL(resolved, window.location.origin).href;
  } catch {
    return new URL('/nails-preview-placeholder.jpg', window.location.origin).href;
  }
}

export default function Cart() {
  const {
    cart,
    updateQuantity,
    removeFromCart,
    clearCart,

    // totals & helpers from context
    subtotalCents,
    shippingCents,
    totalCents,
    formatUSD,

    // cart-level fulfillment
    orderFulfillment,
    setOrderFulfillment,
  } = useCart();

  const [optIn, setOptIn] = useState(true);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }
    if (!isValidEmail(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      setLoading(true);

      const itemsPayload = cart.map((it) => {
        const title =
          it.title || [it.style, it.color?.name || it.color].filter(Boolean).join(' • ');
        const thumb = it.image || it.thumbnail;

        return {
          title,
          priceCents: toPriceCents(it),
          quantity: it.quantity || 1,
          notes: it.notes || '',
          thumbnail: absoluteForStripe(thumb),
          variantKey: it.variantKey || null,
          slug: it.slug,
          sku: it.sku || null,
        };
      });

      const response = await fetch(`${backendUrl}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: itemsPayload,
          optedIn: optIn,
          customer_email: email,
          intent: 'order',
          // tell server how to build shipping on Stripe
          fulfillment: orderFulfillment, // 'shipping' | 'in_person'
          // pass totals if your server wants to validate:
          client_totals: {
            subtotal: subtotalCents,
            shipping: shippingCents,
            total: totalCents,
          },
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert(`Error starting checkout: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Your Cart | NeNeNail’dIt</title>
        <meta
          name="description"
          content="Review your selected custom press-on nail sets. Add, remove, or adjust your order before checkout."
        />
        <meta name="keywords" content="press-on nails cart, NeNe nails order, checkout nails, handmade nails" />
        <meta property="og:title" content="Your Cart | NeNeNail’dIt" />
        <meta property="og:description" content="Review and finalize your custom press-on nail set order." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nenethearchitect.com/nails/cart" />
        <meta property="og:image" content="https://nenethearchitect.com/preview.png" />
      </Helmet>

      <div className="cart-wrapper">
        <div className="cart-page">
          <h1>Your Cart</h1>

          {cart.length === 0 ? (
            <p>Your cart is empty. Please add items to your cart before proceeding.</p>
          ) : (
            <>
              {/* Cart-level fulfillment */}
              <div className="cart-fulfillment">
                <h3>How would you like to get your nails?</h3>
                <div className="fulfillment-options">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="orderFulfillment"
                      value="shipping"
                      checked={orderFulfillment === 'shipping'}
                      onChange={() => setOrderFulfillment('shipping')}
                    />
                    Ship to me (flat rate)
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="orderFulfillment"
                      value="in_person"
                      checked={orderFulfillment === 'in_person'}
                      onChange={() => setOrderFulfillment('in_person')}
                    />
                    In-person pickup / install (no shipping fee)
                  </label>
                </div>
              </div>

              <div className="cart-table-wrapper">
                <table className="cart-table">
                  <thead>
                    <tr>
                      <th>Thumbnail</th>
                      <th>Set</th>
                      <th>Options</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, index) => {
                      const displayThumb = imgSrc(item.image || item.thumbnail);
                      const unit = toPriceCents(item) / 100;
                      const lineTotal = unit * (item.quantity || 1);

                      return (
                        <tr key={index}>
                          <td>
                            <img
                              src={displayThumb}
                              alt={item.title}
                              className="cart-thumbnail"
                              style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10 }}
                              loading="lazy"
                              decoding="async"
                              onError={(e) => (e.currentTarget.src = '/nails-preview-placeholder.jpg')}
                            />
                          </td>
                          <td>
                            <strong>{item.title}</strong>
                            {item.isOneOfOne && (
                              <div className="badge-oneofone" title="One of One">One-of-One</div>
                            )}
                          </td>
                          <td className="text-sm">
                            {item.color?.name && <div>Color: {item.color.name}</div>}
                            {item.shape && <div>Shape: {item.shape}</div>}
                            {item.length && <div>Length: {item.length}</div>}
                          </td>
                          <td>
                            {item.isOneOfOne ? (
                              <span>1</span>
                            ) : (
                              <div className="qty-controls">
                                <button
                                  onClick={() => updateQuantity(index, Math.max(1, (item.quantity || 1) - 1))}
                                  disabled={(item.quantity || 1) === 1}
                                  aria-label="Decrease"
                                >
                                  −
                                </button>
                                <span>{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(index, (item.quantity || 1) + 1)}
                                  aria-label="Increase"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </td>
                          <td>${lineTotal.toFixed(2)}</td>
                          <td>
                            <button className="link danger" onClick={() => removeFromCart(index)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="cart-summary">
                <div className="row">
                  <span>Subtotal</span>
                  <span>{formatUSD(subtotalCents)}</span>
                </div>
                <div className="row">
                  <span>Shipping</span>
                  <span>
                    {orderFulfillment === 'in_person'
                      ? 'FREE (In-Person)'
                      : formatUSD(shippingCents)}
                  </span>
                </div>
                <div className="row total">
                  <span>Total</span>
                  <span>{formatUSD(totalCents)}</span>
                </div>
              </div>

              {/* Email + marketing */}
              <div className="checkout-email-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                  />
                  I'd like to receive updates, nail drops, and discounts.
                </label>
              </div>

              <div className="actions">
                <button onClick={clearCart}>Clear Cart</button>
                <button
                  onClick={handleCheckout}
                  disabled={cart.length === 0 || !isValidEmail(email) || loading}
                >
                  {loading ? 'Redirecting…' : 'Checkout'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}
